import { useCallback, useRef } from 'react';

/**
 * Tracks the last-seen timestamp per raw scanned value in a ref (not React
 * state, deliberately -- this is checked on every decoded frame, potentially
 * many times a second, and must never trigger a re-render itself).
 *
 * This is distinct from and complementary to two other duplicate checks in
 * the system:
 *  - Server-side duplicate-scan rejection (Event.DuplicateScanWindowSeconds,
 *    default 300s) -- a business rule about not logging the same lead twice.
 *  - This hook -- a much shorter (spec: 3s) guard against the SAME physical
 *    scan being registered multiple times because the QR stayed in the
 *    camera's view across several decode frames.
 */
export function useDuplicateGuard(windowMs: number) {
  const lastSeenAtByValue = useRef(new Map<string, number>());

  const shouldAllow = useCallback(
    (value: string): boolean => {
      const now = Date.now();
      const lastSeenAt = lastSeenAtByValue.current.get(value);

      if (lastSeenAt !== undefined && now - lastSeenAt < windowMs) {
        return false;
      }

      lastSeenAtByValue.current.set(value, now);

      // Prevent unbounded growth over a long scanning session (a full
      // conference day could otherwise accumulate thousands of entries).
      if (lastSeenAtByValue.current.size > 500) {
        const cutoff = now - windowMs;
        for (const [key, ts] of lastSeenAtByValue.current) {
          if (ts < cutoff) lastSeenAtByValue.current.delete(key);
        }
      }

      return true;
    },
    [windowMs]
  );

  return shouldAllow;
}
